import { type CalendarSpelling, DEFAULT_CALENDAR_SPELLING } from '@app/shared'
import {
  footerParts,
  letterheadClasses,
  plain,
  StationeryFoot,
  StationeryHead,
} from '@app/stationery'
import type { KeyboardEvent } from 'react'
import { Classification } from './Classification'
import { Conditions } from './Conditions'
import { Formula } from './Formula'
import {
  type Item,
  type ItemEntry,
  isCarriedNight,
  isNoteLine,
  minyanOrdinal,
  rowKey,
  scheduledDays,
  sheetAsides,
  sheetTitleHe,
  withSunset,
} from './layout'
import type {
  LuachDay,
  LuachEntry,
  LuachOptions,
  LuachPart,
  LuachResult,
  LuachShul,
} from './types'

/**
 * Above this many day-sections a chag needs two columns to stay on one letter
 * page. Sukkos (10 days) and Pesach (9) cross it; Shavuos and Purim do not —
 * the same split the workbook's tabs already make by hand.
 */
const TWO_COLUMN_THRESHOLD = 4

/**
 * Splits "6:43 PM" so the meridiem can be set back. The digits are what a
 * person actually reads off a schedule; AM/PM is only there to disambiguate,
 * so it should not compete with them.
 */
function Time({ value }: { value: string }) {
  const m = /^(\d{1,2}:\d{2})\s*(AM|PM)$/i.exec(value.trim())
  if (!m) return <>{value}</>
  return (
    <>
      {m[1]}
      <span className="meridiem">{m[2].toLowerCase()}</span>
    </>
  )
}

/**
 * Optional line selection, for a reader who wants to interrogate the sheet.
 *
 * Kept as PROPS rather than in `LuachOptions` on purpose: options describe the
 * printed artifact and are shared with the PDF, and there is no such thing as
 * clicking a piece of paper. With `onEntryClick` absent — every existing caller,
 * including the renderer — the markup is byte-identical to before, which is the
 * property that lets the sheet stay the printed sheet.
 */
export type LuachSelection = {
  onEntryClick?: (entry: LuachEntry) => void
  /** Entry key currently open, if any. */
  selectedKey?: string | null
}

/**
 * The printed davening schedule. ONE component for every medium — the screen
 * and the PDF differ only in the options passed here (subtitles, civil dates,
 * column count) and in page mechanics. Anything that changes what a line says
 * or where it sorts lives in `layout.ts` and is shared by both.
 */
export function Luach({
  result,
  shul,
  options = {},
  onEntryClick,
  selectedKey,
}: {
  result: LuachResult
  shul: LuachShul
  options?: LuachOptions
} & LuachSelection) {
  const {
    showSubtitle = false,
    showCivilDate = false,
    letterhead,
    masthead = 'full',
    columns = 'auto',
    print = false,
    working = false,
    showConfidence = true,
  } = options

  // A day with nothing scheduled does not get a section.
  const days = scheduledDays(result.days)

  // Which week it is, the month's Rosh Chodesh and molad, and the reasons
  // behind replaced times — one reading shared with every page that sets the
  // same schedule.
  const asides = sheetAsides(result)
  const subtitles = asides.week
  const overrides = asides.overrides

  /*
   * The reasoning sheet is one column whatever it was asked for. Two columns
   * are how a SCHEDULE recovers height for a long chag, and that trade works
   * because a line is a label and a time. Reasoning is prose, and half a page
   * of measure sets it four words to the line.
   */
  const resolvedColumns = working
    ? 1
    : columns === 'auto'
      ? print && days.length > TWO_COLUMN_THRESHOLD
        ? 2
        : 1
      : columns

  /*
   * Whether the sheet has room to spend. A compact masthead is the artifact
   * saying so outright; two columns say it by having been resolved at all —
   * the days only flow into two when one will not hold them. Either way the
   * sheet gives back the stories a roomy one earns, here and in `luach.css`.
   */
  const tight = masthead === 'compact' || resolvedColumns > 1

  /*
   * The address as two lines — place, then how to reach them — which is how the
   * printed sheet sets it. It costs a story, so a sheet short of room runs the
   * two halves together instead.
   */
  const foot = footerParts(shul)
  const twoLineFoot = !tight && !!foot.place && !!foot.contact

  const classes = [
    'luach',
    print ? 'luach--print' : '',
    // The reasoning sheet is a different artifact from the wall luach, not the
    // wall luach with extra rows: it flows to as many pages as it takes, and
    // `print.css` reads this to know which of the two it is laying out.
    working ? 'luach--reasoning' : '',
    showSubtitle ? '' : 'luach--no-subtitle',
    // The column count on the SHEET as well as on the body: the letterhead and
    // the title sit above the body and rule themselves to the width of the
    // schedule, which is one measure in a column and the whole page in two.
    `luach--cols-${resolvedColumns}`,
    // On the sheet rather than on the masthead: the footer reads the same
    // properties, so the page opens and closes on one figure.
    ...letterheadClasses(letterhead, masthead),
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <article className={classes}>
      <header className="luach__head">
        {/*
         * The congregation's letterhead: who this sheet is from. It is the same
         * on every sheet the shul ever prints, so it reads as stationery — set
         * quietly and closed off by a heavy rule, with the schedule beginning
         * below it rather than continuing from it.
         */}
        <StationeryHead shul={shul} />

        {/*
         * What THIS sheet is — the line read from across the room.
         *
         * No date span under it: the span is the first day heading's date and
         * the last one's, and each day states its own where a reader looks for
         * it. A sheet that prints the same dates twice is a sheet the eye has
         * to reconcile.
         */}
        <div className="luach__titles">
          {/* A stationery that BANDS the title bands this line alone. What
              qualifies the week reads under the band, not inside it. */}
          <h1 className="luach__title">{plain(sheetTitleHe(result))}</h1>
          {subtitles.length > 0 ? (
            <div className="luach__special">{plain(subtitles.join(' · '))}</div>
          ) : null}
        </div>
      </header>

      <div className={`luach__body luach__body--cols-${resolvedColumns}`}>
        {days.map((day) => (
          <DaySection
            key={day.date}
            day={day}
            spelling={shul.calendarSpelling ?? DEFAULT_CALENDAR_SPELLING}
            working={working}
            showConfidence={showConfidence}
            showSubtitle={showSubtitle}
            showCivilDate={showCivilDate}
            onEntryClick={onEntryClick}
            selectedKey={selectedKey}
          />
        ))}
      </div>

      {asides.months.map((m) => (
        <div className="mevorchim" key={m.key}>
          {m.roshChodesh ? (
            <div className="mevorchim__rc">{m.roshChodesh}</div>
          ) : null}
          <div className="mevorchim__molad">{m.molad}</div>
        </div>
      ))}

      {overrides.length > 0 ? (
        <div className="luach__notes">
          {overrides.map((n) => (
            <p className="luach__note" key={n}>
              <span aria-hidden>*</span> {n}
            </p>
          ))}
        </div>
      ) : null}

      {result.greetingHe ? (
        <div className="luach__greeting">{plain(result.greetingHe)}</div>
      ) : null}

      {/* The address inside its own element, so a form that puts the line
          THROUGH it — split, inline, measured — has something to run either
          side of. The contact half is a sibling rather than a second child of
          it, so those forms rule one line and not both. */}
      <StationeryFoot shul={shul} twoLine={twoLineFoot} />
    </article>
  )
}

/**
 * One day of the schedule. Kept whole: in two-column print this is the unit
 * that must not break across the column boundary, or a day's times end up
 * split between two places on the page.
 */
function DaySection({
  day,
  spelling,
  working,
  showConfidence,
  showSubtitle,
  showCivilDate,
  onEntryClick,
  selectedKey,
}: {
  day: LuachDay
  spelling: CalendarSpelling
  working: boolean
  showConfidence: boolean
  showSubtitle: boolean
  showCivilDate: boolean
} & LuachSelection) {
  return (
    <section className="day">
      <div className="day__head">
        <h2 className="day__name">{plain(day.dayLabelHe)}</h2>
        {/*
         * The date, in ONE language. The sheet is Hebrew, so the Hebrew date is
         * set in Hebrew letters — a transliteration is English, and printing it
         * beside a Hebrew day name puts two scripts in a line that states one
         * fact. Turning English on switches the whole line: the transliterated
         * Hebrew date AND the civil date, which are then the same language as
         * each other.
         */}
        <span className={`day__date${showCivilDate ? '' : ' day__date--he'}`}>
          {showCivilDate
            ? `${day.hebrewDate} · ${new Date(
                `${day.date}T12:00:00`,
              ).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}`
            : day.hebrewDateHe}
        </span>
      </div>

      {working && day.flags.length > 0 && (
        <div className="flags">
          {day.flags.map((f) => (
            <span className="flag" key={f}>
              {f}
            </span>
          ))}
        </div>
      )}

      <div className="rows">
        {withSunset(day).map((item, i, all) =>
          item.kind === 'anchor' ? (
            /*
             * Shkia is an ordinary row, the way the workbook prints it — label
             * right, time left, in line with everything else. It was a centered
             * divider with rules across the sheet, which cost a row's height
             * several times over and gave a reference line more voice than the
             * davening times it exists to explain.
             */
            <div className="row row--anchor" key={`${day.date}-shkia`}>
              <div className="row__label">שקיעה</div>
              <div className="row__time tabular">
                <Time value={item.time} />
              </div>
            </div>
          ) : (
            <Row
              key={rowKey(day.date, item.entry)}
              entry={item.entry}
              spelling={spelling}
              working={working}
              showConfidence={showConfidence}
              showSubtitle={showSubtitle}
              onEntryClick={onEntryClick}
              selectedKey={selectedKey}
              // A rule below the last carried-over evening line marks where the
              // night ends and the day proper begins. Only where the day proper
              // follows: a section that is ALL carried night — motzei Shabbos —
              // has nothing on the far side for the rule to divide it from.
              dayBreakAfter={
                isCarriedNight(item.entry) &&
                all[i + 1] !== undefined &&
                !(
                  all[i + 1]?.kind === 'entry' &&
                  isCarriedNight((all[i + 1] as ItemEntry).entry)
                )
              }
            />
          ),
        )}
      </div>
    </section>
  )
}

function Row({
  entry,
  spelling,
  working,
  showConfidence,
  showSubtitle,
  dayBreakAfter,
  onEntryClick,
  selectedKey,
}: {
  entry: LuachEntry
  spelling: CalendarSpelling
  working: boolean
  showConfidence: boolean
  showSubtitle: boolean
  dayBreakAfter?: boolean
} & LuachSelection) {
  // A reminder or announcement carries its whole meaning in its text — it gets
  // the full width rather than a label/time pair that would print it twice.
  if (isNoteLine(entry)) {
    return (
      <div className="row--note">
        <span>{entry.noteText}</span>
      </div>
    )
  }

  // A line either has a clock time, or a phrase standing in for one
  // ("אחר מנחה"). Never show both — the phrase is what the shul prints.
  const phrase = entry.displayText ?? entry.noteText
  // A composite prints several opinions on one line: the shared label, then each
  // sub-label joined by "/", and the times likewise. The parts carry the times.
  const parts = entry.parts?.length ? entry.parts : null
  const showTime = parts ? true : entry.time && !phrase
  const subLabels = (get: (p: LuachPart) => string) =>
    parts ? ` - ${parts.map(get).join(' / ')}` : ''

  // A note line carries no rule, so there is nothing to open — it stays inert
  // even when the rest of the sheet is interrogable.
  const interactive = !!onEntryClick
  const selected = interactive && selectedKey === entry.key

  return (
    <div
      className={[
        'row',
        dayBreakAfter ? 'row--daybreak' : '',
        interactive ? 'row--pick' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-open={selected ? '' : undefined}
      // A div rather than a button: `.row` is a grid whose columns align the
      // label and the time down the whole sheet, and a button between them
      // breaks that alignment. Clickable rows are a controlled pattern here and
      // the lint rule is configured off in biome.json rather than ignored inline.
      {...(interactive
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-pressed': selected,
            onClick: () => onEntryClick?.(entry),
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onEntryClick?.(entry)
              }
            },
          }
        : {})}
    >
      <div className="row__label">
        {entry.minyanIndex ? (
          <span className="row__minyan">
            {minyanOrdinal(entry.minyanIndex)}
          </span>
        ) : null}
        {entry.labelHe}
        {subLabels((p) => p.labelHe)}
        {showSubtitle ? (
          <span className="row__gloss">
            {entry.labelEn}
            {subLabels((p) => p.labelEn)}
            {working && showConfidence && entry.needsReview
              ? ' · needs review'
              : ''}
          </span>
        ) : null}
        {/* Where it is, when it is not the usual place. A null room prints
            nothing: naming the main shul on every line of an ordinary Shabbos
            is noise, and the room only means something once a day has more
            than one. Hebrew, beside the Hebrew label — unlike `subText`, which
            is an address and sets its own direction. */}
        {entry.room ? (
          <span className="row__room">{entry.room.name}</span>
        ) : null}
        {entry.subText ? (
          <span className="row__sub">{entry.subText}</span>
        ) : null}
      </div>
      <div className={`row__time ${phrase ? 'row__time--phrase' : 'tabular'}`}>
        {parts ? (
          // RTL ordering so the first time sits under the first sub-label.
          <span dir="rtl">
            {parts.map((p, i) => (
              <span key={p.key}>
                {i > 0 ? <span className="row__pair-sep">/</span> : null}
                {p.time ? <Time value={p.time} /> : '—'}
              </span>
            ))}
          </span>
        ) : showTime && entry.time ? (
          <Time value={entry.time} />
        ) : (
          phrase
        )}
        {entry.overrideNote ? (
          <span className="row__override" aria-hidden>
            *
          </span>
        ) : null}
      </div>
      {working && (
        <>
          {/* What this line IS. Only in the logic view — the printed sheet says
              the time, not the taxonomy. */}
          <div className="row__classify">
            <Classification rule={entry} spelling={spelling} />
          </div>
          {/* Only anchor-based lines have a formula worth showing; a fixed time
              just repeats the time column, so it earns no logic row. */}
          {entry.formula.kind === 'offset' ||
          entry.formula.kind === 'sequence' ? (
            <div className="row__basis">
              <Formula spec={entry.formula} />
              {entry.formula.anchorTime ? (
                <span className="row__basetime">
                  {' '}
                  ({entry.formula.anchor} {entry.formula.anchorTime}
                  {/* A carried night resolves on the day BEFORE the one it
                      prints under, so its anchor is the previous evening's.
                      Unsaid, the sheet shows a sunset under a heading whose own
                      sunset is a different time, and the reader has no way to
                      tell — which on the sheet built for checking times is the
                      whole failure. */}
                  {isCarriedNight(entry) ? ', erev' : ''})
                </span>
              ) : null}
              {entry.time && phrase ? ` · resolves to ${entry.time}` : ''}
            </div>
          ) : null}
          {entry.requireFlags?.length ||
          entry.excludeFlags?.length ||
          entry.conditionLabel ? (
            <div className="row__gate">
              <Conditions
                conditions={[
                  { key: 'Only on', values: entry.requireFlags ?? [] },
                  { key: 'Not on', values: entry.excludeFlags ?? [] },
                  {
                    key: 'Only when',
                    values: entry.conditionLabel ? [entry.conditionLabel] : [],
                  },
                ]}
              />
            </div>
          ) : null}
          {entry.rationale ? (
            <div className="row__why">{entry.rationale}</div>
          ) : null}
          {/*
           * How much this line is to be trusted, and what to ask about it.
           *
           * Only a rule still awaiting an answer says anything: a settled rule
           * marking itself settled on every line would be noise on a sheet
           * whose whole job is to make the unsettled ones findable. Provenance
           * rides along only in that case for the same reason — 'assumed'
           * matters, 'gabbai' is what the reader already assumes.
           */}
          {showConfidence && entry.needsReview ? (
            <div className="row__check">
              <span className="row__check-mark">Needs review</span>
              {entry.provenance ? (
                <span className="row__check-source">
                  {provenanceLabel(entry.provenance)}
                </span>
              ) : null}
              {entry.openQuestion ? (
                <span className="row__check-ask">{entry.openQuestion}</span>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

/**
 * Where a number came from, said in words on the reasoning sheet.
 *
 * Terser than the rule editor's wording (`rule-provenance.ts`) on purpose: the
 * editor is explaining a choice somebody is about to make, and this is a note
 * beside a line somebody is checking. An unrecognized value prints as itself —
 * the enum can gain a member without the sheet losing the field.
 */
function provenanceLabel(provenance: string): string {
  return (
    {
      assumed: 'assumed',
      sheet_literal: 'hand-typed in the workbook',
      sheet_formula: 'from a workbook formula',
      gabbai: 'confirmed with the shul',
    }[provenance] ?? provenance
  )
}

export type { Item }
